import { useQuery } from '@tanstack/react-query';
import { useUniversalNavContext } from './useUniversalNavContext';

type ZapierAccountEntitlementsRaw = {
  limits: {
    is_alerts_center_enabled?: boolean;
    is_analytics_dashboard_enabled?: boolean;
    pay_per_task_enabled: boolean;
    is_approval_requests_enabled: boolean;
  };
  quotas: {
    tasks: {
      count: number;
      limit: number;
    };
  };
};

export type ZapierAccountEntitlements = {
  limits: {
    isAlertsCenterEnabled?: boolean;
    isAnalyticsDashboardEnabled?: boolean;
    isApprovalRequestsEnabled?: boolean;
    payPerTaskEnabled: boolean;
  };
  quotas: {
    tasks: {
      count: number;
      limit: number;
    };
  };
};

const infinitize = (num: number) => (num === -1 ? Infinity : Number(num));

const prepareEntitlementsData = (
  rawData: ZapierAccountEntitlementsRaw
): ZapierAccountEntitlements => {
  const output = {
    limits: {
      isAlertsCenterEnabled: rawData.limits.is_alerts_center_enabled,
      isAnalyticsDashboardEnabled:
        rawData.limits.is_analytics_dashboard_enabled,
      payPerTaskEnabled: rawData.limits.pay_per_task_enabled,
      isApprovalRequestsEnabled: rawData.limits.is_approval_requests_enabled,
    },
    quotas: {
      tasks: {
        count: rawData.quotas.tasks.count,
        limit: infinitize(rawData.quotas.tasks.limit),
      },
    },
  };

  return output;
};

export const useQueryEntitlements = (currentAccountId?: number) => {
  const { apiHostname } = useUniversalNavContext();

  const result = useQuery({
    queryKey: ['entitlements', currentAccountId] as const,
    queryFn: async () => {
      if (!currentAccountId) {
        return undefined;
      }

      const encodedAccountId = encodeURIComponent(currentAccountId);

      const url = new URL(
        `/api/v4/accounts/${encodedAccountId}/entitlements`,
        apiHostname || window.location.origin
      );

      const response = await fetch(url.toString(), {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const json = await response.json();

      return json;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour
    select: prepareEntitlementsData,
    enabled: !!currentAccountId,
  });

  return {
    ...result,
    isLoading: result.isInitialLoading,
  };
};
